import React, { useEffect, useState } from "react";
import Header from "../../components/ui/Header";
import NavigationBreadcrumb from "../../components/ui/NavigationBreadcrumb";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Icon from "../../components/AppIcon";
import {
  getAllSchedules,
  markSessionConducted,
  setParticipantRemark,
  listSessionObjections,
  resolveSessionObjection,
} from "../../api_service";

/**
 * Feature C — Training Sessions: trainers mark sessions conducted and record
 * per-participant attendance + remarks; managers resolve trainee objections.
 */
const TrainingSessions = () => {
  const [sessions, setSessions] = useState([]); // grouped by scheduleId
  const [objections, setObjections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const loadSessions = async () => {
    try {
      const res = await getAllSchedules();
      const rows = res?.data?.data || res?.data || [];
      // group participant rows by their session (scheduleId)
      const map = new Map();
      rows.forEach((r) => {
        const s = r.interviewSchedule || {};
        const sid = s.scheduleId;
        if (sid == null) return;
        if (!map.has(sid)) {
          map.set(sid, { session: s, participants: [] });
        }
        map.get(sid).participants.push({
          mapId: r.id,
          name: r.user ? `${r.user.firstname || ""} ${r.user.lastname || ""}`.trim() : (r.user?.trngid || "—"),
          trngid: r.user?.trngid,
          attended: !!r.attended,
          trainerRemark: r.trainerRemark || "",
        });
      });
      setSessions(Array.from(map.values()).sort((a, b) => b.session.scheduleId - a.session.scheduleId));
    } catch (e) {
      console.error("Failed to load sessions", e);
    }
  };

  const loadObjections = async () => {
    try {
      const res = await listSessionObjections();
      setObjections(res?.data?.data || []);
    } catch (e) {
      console.error("Failed to load objections", e);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadSessions(), loadObjections()]);
      setLoading(false);
    })();
  }, []);

  const handleConduct = async (scheduleId, conducted) => {
    setSaving(`conduct-${scheduleId}`);
    try {
      await markSessionConducted(scheduleId, conducted);
      await loadSessions();
    } catch (e) {
      alert("Failed to update session");
    } finally {
      setSaving(null);
    }
  };

  const handleParticipantSave = async (p) => {
    setSaving(`p-${p.mapId}`);
    try {
      await setParticipantRemark(p.mapId, p.attended, p.trainerRemark);
    } catch (e) {
      alert("Failed to save participant");
    } finally {
      setSaving(null);
    }
  };

  const updateParticipant = (sid, mapId, field, value) => {
    setSessions((prev) =>
      prev.map((g) =>
        g.session.scheduleId !== sid
          ? g
          : { ...g, participants: g.participants.map((p) => (p.mapId === mapId ? { ...p, [field]: value } : p)) }
      )
    );
  };

  const handleResolve = async (id, status) => {
    const remark = window.prompt(`${status === "RESOLVED" ? "Resolution" : "Rejection"} note (optional):`) || "";
    setSaving(`o-${id}`);
    try {
      await resolveSessionObjection(id, status, remark);
      await loadObjections();
    } catch (e) {
      alert("Failed to resolve objection");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <NavigationBreadcrumb userRole="manager" className="mb-6" />
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-1">Training Sessions</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Mark sessions conducted, record attendance &amp; remarks, and resolve trainee objections.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-20 text-muted-foreground">Loading…</div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* Sessions */}
              <div className="xl:col-span-2 space-y-5">
                {sessions.length === 0 && (
                  <div className="bg-card border border-border rounded-2xl p-6 text-muted-foreground">No sessions found.</div>
                )}
                {sessions.map(({ session, participants }) => (
                  <div key={session.scheduleId} className="bg-card border border-border rounded-2xl p-5">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div>
                        <h3 className="font-semibold text-foreground">
                          {session.interviewType || "Session"} · {session.date} {session.time}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {session.location || "—"} · {session.duration || 0} min
                        </p>
                      </div>
                      <Button
                        variant={session.conducted ? "success" : "default"}
                        iconName={session.conducted ? "CheckCircle" : "Circle"}
                        iconPosition="left"
                        disabled={saving === `conduct-${session.scheduleId}`}
                        onClick={() => handleConduct(session.scheduleId, !session.conducted)}
                      >
                        {session.conducted ? "Conducted" : "Mark conducted"}
                      </Button>
                    </div>

                    <div className="space-y-3">
                      {participants.map((p) => (
                        <div key={p.mapId} className="border border-border rounded-xl p-3 bg-white/40">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-foreground">{p.name || p.trngid}</span>
                            <label className="flex items-center gap-2 text-sm text-muted-foreground">
                              <input
                                type="checkbox"
                                checked={p.attended}
                                onChange={(e) => updateParticipant(session.scheduleId, p.mapId, "attended", e.target.checked)}
                              />
                              Attended
                            </label>
                          </div>
                          <div className="flex gap-2">
                            <Input
                              placeholder="Remark for this trainee…"
                              value={p.trainerRemark}
                              onChange={(e) => updateParticipant(session.scheduleId, p.mapId, "trainerRemark", e.target.value)}
                            />
                            <Button
                              variant="outline"
                              iconName="Save"
                              disabled={saving === `p-${p.mapId}`}
                              onClick={() => handleParticipantSave(p)}
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Objections */}
              <div className="space-y-4">
                <div className="bg-card border border-border rounded-2xl p-5">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="AlertTriangle" size={18} /> Trainee Objections
                  </h3>
                  {objections.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No objections raised.</p>
                  ) : (
                    <div className="space-y-3">
                      {objections.map((o) => (
                        <div key={o.id} className="border border-border rounded-xl p-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{o.trainee?.trngid || "trainee"}</span>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full ${
                                o.status === "PENDING"
                                  ? "bg-amber-100 text-amber-700"
                                  : o.status === "RESOLVED"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {o.status}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{o.reason}</p>
                          {o.status === "PENDING" && (
                            <div className="flex gap-2 mt-2">
                              <Button variant="success" iconName="Check" disabled={saving === `o-${o.id}`}
                                onClick={() => handleResolve(o.id, "RESOLVED")}>Resolve</Button>
                              <Button variant="destructive" iconName="X" disabled={saving === `o-${o.id}`}
                                onClick={() => handleResolve(o.id, "REJECTED")}>Reject</Button>
                            </div>
                          )}
                          {o.resolutionRemark && (
                            <p className="text-xs text-muted-foreground mt-1 italic">Note: {o.resolutionRemark}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TrainingSessions;
